import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import IncompleteCircleIcon from "@mui/icons-material/IncompleteCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Box, Typography } from "@mui/material";

export type ActionStatus = "completed" | "on-track" | "submitted" | "other";

export function normalizeActionStatus(raw: string | undefined): ActionStatus {
  if (raw === "completed" || raw === "on-track" || raw === "submitted") {
    return raw;
  }
  return "other";
}

interface ActionStatusPresentation {
  label: string;
  bg: string;
  fg: string;
  icon: React.ComponentType<{ sx?: object }>;
}

const PRESENTATION: Record<
  Exclude<ActionStatus, "submitted" | "other">,
  ActionStatusPresentation
> = {
  completed: {
    label: "Done",
    bg: "#dcfce7",
    fg: "#15803d",
    icon: CheckCircleIcon,
  },
  "on-track": {
    label: "On track",
    bg: "#e0f2fe",
    fg: "#0369a1",
    icon: TrendingUpIcon,
  },
};

/**
 * Compact pill, intended for table rows. Renders nothing for statuses without
 * a distinct visualization (submitted/other), so the default row stays clean.
 */
export function ActionStatusPill({
  status,
  size = "sm",
}: {
  status: ActionStatus;
  size?: "sm" | "md";
}) {
  if (status === "submitted" || status === "other") return null;
  const p = PRESENTATION[status];
  const Icon = p.icon;
  const sm = size === "sm";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        bgcolor: p.bg,
        color: p.fg,
        borderRadius: "999px",
        px: sm ? 1 : 1.25,
        py: sm ? 0.25 : 0.5,
        fontSize: sm ? "0.7rem" : "0.8rem",
        fontWeight: 600,
        lineHeight: 1.2,
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: sm ? 12 : 16 }} />
      <Typography
        component="span"
        sx={{ fontSize: "inherit", fontWeight: "inherit", lineHeight: 1 }}
      >
        {p.label}
      </Typography>
    </Box>
  );
}

/**
 * Subtle left-border accent color for a row, so users can scan the list and
 * spot tracked/done items at a glance without colour-coding the whole row.
 */
export function actionStatusAccent(status: ActionStatus): string | undefined {
  if (status === "completed") return "#15803d";
  if (status === "on-track") return "#0369a1";
  return undefined;
}

interface StatusMarker {
  icon: React.ComponentType<{ sx?: object; "aria-label"?: string }>;
  color: string;
  label: string;
}

// Leading marker for action rows — a tick for done, a half-filled circle for
// in-progress, an empty circle for not-yet-started. Carries the status so the
// rows don't need a coloured highlight to read at a glance.
const STATUS_MARKER: Record<ActionStatus, StatusMarker> = {
  completed: { icon: CheckCircleIcon, color: "#15803d", label: "Completed" },
  "on-track": {
    icon: IncompleteCircleIcon,
    color: "#0369a1",
    label: "In progress",
  },
  submitted: {
    icon: RadioButtonUncheckedIcon,
    color: "#d4d4d8",
    label: "Not started",
  },
  other: {
    icon: RadioButtonUncheckedIcon,
    color: "#d4d4d8",
    label: "Not started",
  },
};

export function ActionStatusIcon({
  status,
  size = 15,
  sx,
}: {
  status: ActionStatus;
  size?: number;
  sx?: object;
}) {
  const marker = STATUS_MARKER[status];
  const Icon = marker.icon;
  return (
    <Icon
      aria-label={marker.label}
      sx={{ fontSize: size, color: marker.color, flexShrink: 0, ...sx }}
    />
  );
}

/**
 * Compact, warm date label for the action row right rail: "May 2027".
 * Returns null when the deadline is missing or unparseable.
 */
export function formatActionDueMonth(deadline?: string): string | null {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

/**
 * Splits "Lead sentence (clarifier)" into [lead, clarifier?] so callers can
 * render the parenthetical on a softer second line. Returns [text, null] when
 * the title doesn't end with a parenthetical clause.
 */
export function splitActionTitle(text: string): [string, string | null] {
  const m = text.match(/^(.+?)\s*\((.+)\)\s*$/);
  if (!m) return [text, null];
  return [m[1], m[2]];
}

/**
 * Sort rank for action rows: completed first, then on-track, then submitted,
 * then unknown. Pass to `Array.prototype.sort` via `(a, b) => rank(a) - rank(b)`.
 */
export function actionStatusRank(status: ActionStatus): number {
  if (status === "completed") return 0;
  if (status === "on-track") return 1;
  if (status === "submitted") return 2;
  return 3;
}

/**
 * Inline "3 actions (1 done, 1 on track)" caption for a category header.
 * The parenthetical makes it clear the breakdown is a subset of the total;
 * we omit the parens entirely when nothing is tracked yet. Colours the
 * done/on-track words to match the row dots.
 */
export function CategoryStatusSummary({
  actions,
}: {
  actions: ReadonlyArray<{ status: ActionStatus }>;
}) {
  const total = actions.length;
  const done = actions.filter((a) => a.status === "completed").length;
  const onTrack = actions.filter((a) => a.status === "on-track").length;

  const breakdown: React.ReactNode[] = [];
  if (done > 0) {
    breakdown.push(
      <Box
        component="span"
        key="done"
        sx={{ color: "#15803d", fontWeight: 600 }}
      >
        {done} done
      </Box>
    );
  }
  if (onTrack > 0) {
    breakdown.push(
      <Box
        component="span"
        key="on-track"
        sx={{ color: "#0369a1", fontWeight: 600 }}
      >
        {onTrack} on track
      </Box>
    );
  }

  return (
    <Typography variant="caption" sx={{ color: "#71717a" }}>
      {total} action{total !== 1 ? "s" : ""}
      {breakdown.length > 0 && (
        <Box component="span">
          {" ("}
          {breakdown.map((seg, i) => (
            <Box component="span" key={i}>
              {i > 0 && ", "}
              {seg}
            </Box>
          ))}
          {")"}
        </Box>
      )}
    </Typography>
  );
}
